import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
} from '@angular/core';
import { PushPipe } from '@ngrx/component';
import { Envelope, SystemEntity } from '@wdx/clmi/clmi-swagger-gen';
import { ModalManagementService } from '@wdx/clmi/ui';
import {
    WdxDateFormat,
    WdxDateTimeService,
    WdxStateManagementClass,
} from '@wdx/shared/utils';
import { BehaviorSubject, Observable, takeUntil } from 'rxjs';
import { AtomModalWrapperModule } from '../../../../../../components/atoms/atom-modal-wrapper/atom-modal-wrapper.module';
import { MoleculeInfoCardSearchModule } from '../../../../../../components/molecules/molecule-info-card-search/molecule-info-card-search.module';
import { OrganismModalModule } from '../../../../../../components/organisms/organism-modal/organism-modal.module';
import { ICON_ENVELOPE } from '../../../../../../constants/icons.constants';
import { InfoCardData } from '../../../../../../models/info-card-data.model';
import { InfoCardSearchMode } from '../../../../../../models/info-card-search-mode.model';
import { EnvelopeFacadeService } from '../../../../../../state/envelope/envelope-facade.service';
import { ENVELOPE_LOOKUP_MODAL_ID } from './envelope-lookup.static';

@Component({
    selector: 'clmi-envelope-lookup',
    templateUrl: './envelope-lookup.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,

    imports: [
        AtomModalWrapperModule,
        MoleculeInfoCardSearchModule,
        OrganismModalModule,
        PushPipe,
    ],
})
export class EnvelopeLookupComponent
    extends WdxStateManagementClass
    implements OnInit
{
    @Input() entity: SystemEntity;
    @Input() entityId: string;

    @Output() confirm = new EventEmitter<Envelope[]>();

    lookupCards$ = new BehaviorSubject<InfoCardData[]>([]);

    envelopes$: Observable<Envelope[]>;
    isLoading$: Observable<boolean>;
    hasError$: Observable<boolean>;

    readonly MODAL_TITLE = 'Add To Envelope(s)';
    readonly MODE = InfoCardSearchMode.MultiSelect;
    readonly ENVELOPE_LOOKUP_MODAL_ID = ENVELOPE_LOOKUP_MODAL_ID;

    constructor(
        private envelopeFacade: EnvelopeFacadeService,
        private modalManagementService: ModalManagementService,
        private wdxDateTimeService: WdxDateTimeService,
    ) {
        super();
    }

    ngOnInit(): void {
        this.envelopes$ = this.envelopeFacade.selectGetForEntityId$(
            this.entityId,
            this.entity,
        );
        this.isLoading$ =
            this.envelopeFacade.selectGetForEntityIdIsLoadingList$(
                this.entityId,
                this.entity,
            );
        this.hasError$ =
            this.envelopeFacade.selectGetForEntityIdHasLoadingListError$(
                this.entityId,
                this.entity,
            );

        this.envelopes$
            .pipe(takeUntil(this.destroyed$))
            .subscribe((envelopes) => {
                this.createLookupFromEnvelopes(envelopes);
            });

        this.modalManagementService.latestInstanceParams$
            .pipe(takeUntil(this.destroyed$))
            .subscribe((params) => {
                if (params.modalId === ENVELOPE_LOOKUP_MODAL_ID) {
                    this.dispatchIfFalsy(this.envelopes$, () => {
                        this.envelopeFacade.dispatchGetForEntityId(
                            this.entityId,
                            this.entity,
                            'draft',
                        );
                    });
                }
            });
    }

    createLookupFromEnvelopes(envelopes: Envelope[]): void {
        if (!envelopes?.length) {
            return;
        }
        this.lookupCards$.next(
            envelopes.reduce(
                (accumulator, envelope) => [
                    ...accumulator,
                    {
                        primaryInfo: envelope.name,
                        tertiaryInfo: [
                            envelope.status,
                            `Created: ${this.wdxDateTimeService.convertDateToViewFriendlyFormat(
                                envelope.createdOn,
                                { format: WdxDateFormat.AbsoluteDateTime },
                            )}`,
                            ...(envelope.completed
                                ? [
                                      `Completed: ${this.wdxDateTimeService.convertDateToViewFriendlyFormat(
                                          envelope.completed,
                                          {
                                              format: WdxDateFormat.AbsoluteDateTime,
                                          },
                                      )}`,
                                  ]
                                : []),
                        ],
                        icon: ICON_ENVELOPE.icon,
                        id: envelope.id,
                        data: envelope,
                    },
                ],
                [],
            ),
        );
    }

    onChange(envelopes: Envelope[]): void {
        this.confirm.emit(envelopes);
    }

    onConfirm(): void {
        this.modalManagementService.closeActiveModal();
    }
}
